import { useCallback, useEffect, useState } from "react";
import { useDataProvider, useNotify } from "react-admin";
import type { RaRecord } from "react-admin";
import { Views } from "react-big-calendar";
import dayjs, { type Dayjs } from "dayjs";
import "@/utils/dayjs-localizer";
import { DatePattern, formatIsoDate } from "@/utils/date-format";
import { formatRazaEventStartTimeLabel } from "@/utils/raza-event-time";
import {
  isOhbatRazaEvent,
  razaEventKindLabel,
  razaEventPurposeLabel,
  razaEventTitle,
} from "@/utils/raza-event-display";
import { mapHallBookingsToRazaCalendarOverlay } from "./map-hall-overlay-events";
import type { RazaEventsCalendarEvent } from "./types";

export function useRazaEventsCalendarEvents(
  view: string,
  date: Dayjs,
  options?: { includeHallOverlay?: boolean }
) {
  const includeHallOverlay = Boolean(options?.includeHallOverlay);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [events, setEvents] = useState<RazaEventsCalendarEvent[]>([]);
  const dateKey = formatIsoDate(dayjs(date));

  const fetchMajlis = useCallback(
    async (start: Date, end: Date) => {
      const { data } = await dataProvider.getList("raza-events", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "date", order: "ASC" },
        filter: {
          kind: "all",
          start: formatIsoDate(dayjs(start)),
          end: formatIsoDate(dayjs(end)),
        },
      });
      return data as RaRecord[];
    },
    [dataProvider]
  );

  const fetchHallBookings = useCallback(
    async (start: Date, end: Date) => {
      const { data } = await dataProvider.getList("hallBookings", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "date", order: "ASC" },
        filter: {
          start: formatIsoDate(dayjs(start)),
          end: formatIsoDate(dayjs(end)),
          includeBlockedDates: true,
        },
      });
      return data as unknown[];
    },
    [dataProvider]
  );

  const loadEvents = useCallback(async () => {
    const selectedDate = dayjs(dateKey);
    let rangeStart: Dayjs;
    let rangeEnd: Dayjs;

    if (view === Views.MONTH) {
      rangeStart = selectedDate.startOf("month");
      rangeEnd = selectedDate.endOf("month");
    } else if (view === Views.WEEK) {
      rangeStart = selectedDate.startOf("week");
      rangeEnd = selectedDate.endOf("week");
    } else {
      rangeStart = selectedDate.startOf("day");
      rangeEnd = selectedDate.endOf("day");
    }

    try {
      const rows = await fetchMajlis(rangeStart.toDate(), rangeEnd.toDate());

      const formatted: RazaEventsCalendarEvent[] = rows.map((row) => {
        const d = formatIsoDate(row.date as string);
        const raw = (row.startTime as string) || "09:00";
        const [hStr, mStr] = raw.split(":");
        const startH = Number(hStr);
        const startM = Number(mStr);
        const start = dayjs(d, DatePattern.ISO_DATE)
          .hour(Number.isFinite(startH) ? startH : 9)
          .minute(Number.isFinite(startM) ? startM : 0)
          .second(0)
          .toDate();
        const end = dayjs(start).add(1, "hour").toDate();
        const timeLabel = formatRazaEventStartTimeLabel(raw);
        const subTitle = isOhbatRazaEvent(row)
          ? `${razaEventKindLabel(row)} · ${timeLabel}`
          : [razaEventPurposeLabel(row) || razaEventKindLabel(row), timeLabel]
              .filter(Boolean)
              .join(" · ");
        return {
          id: row.id,
          title: razaEventTitle(row),
          subTitle,
          start,
          end,
          resource: row,
        };
      });

      if (includeHallOverlay) {
        try {
          const hallRows = await fetchHallBookings(rangeStart.toDate(), rangeEnd.toDate());
          formatted.push(...mapHallBookingsToRazaCalendarOverlay(hallRows));
        } catch {
          notify("Error loading hall bookings for overlay", { type: "warning" });
        }
      }

      setEvents(formatted);
    } catch {
      notify("Error loading raza events", { type: "warning" });
    }
  }, [view, dateKey, fetchMajlis, fetchHallBookings, includeHallOverlay, notify]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      void loadEvents();
    }, 300);
    return () => clearTimeout(timeout);
  }, [loadEvents]);

  return events;
}
